import { Directive, ElementRef, Renderer2, OnInit, OnDestroy } from '@angular/core';

@Directive({
  selector: '[appColumnResize]',
  standalone: true
})
export class ColumnResizeDirective implements OnInit, OnDestroy {
  private resizeHandle!: HTMLElement;
  private isResizing = false;
  private startX = 0;
  private startWidth = 0;
  private minWidth = 80;
  private boundMouseMove!: (event: MouseEvent) => void;
  private boundMouseUp!: (event: MouseEvent) => void;

  constructor(
    private el: ElementRef,
    private renderer: Renderer2
  ) {}

  ngOnInit(): void {
    this.createResizeHandle();
    this.boundMouseMove = this.onMouseMove.bind(this);
    this.boundMouseUp = this.onMouseUp.bind(this);
  }

  ngOnDestroy(): void {
    if (this.resizeHandle) {
      this.resizeHandle.removeEventListener('mousedown', this.onMouseDown.bind(this));
      this.resizeHandle.remove();
    }
    document.removeEventListener('mousemove', this.boundMouseMove);
    document.removeEventListener('mouseup', this.boundMouseUp);
  }

  private createResizeHandle(): void {
    this.resizeHandle = this.renderer.createElement('div');
    this.renderer.addClass(this.resizeHandle, 'resize-handle');
    this.renderer.setStyle(this.resizeHandle, 'position', 'absolute');
    this.renderer.setStyle(this.resizeHandle, 'top', '0');
    this.renderer.setStyle(this.resizeHandle, 'right', '0');
    this.renderer.setStyle(this.resizeHandle, 'width', '8px');
    this.renderer.setStyle(this.resizeHandle, 'height', '100%');
    this.renderer.setStyle(this.resizeHandle, 'cursor', 'col-resize');
    this.renderer.setStyle(this.resizeHandle, 'z-index', '10');
    this.renderer.setStyle(this.resizeHandle, 'user-select', 'none');

    this.renderer.setStyle(this.el.nativeElement, 'position', 'relative');
    this.renderer.setStyle(this.el.nativeElement, 'user-select', 'none');
    this.renderer.appendChild(this.el.nativeElement, this.resizeHandle);

    this.resizeHandle.addEventListener('mousedown', this.onMouseDown.bind(this));

    this.resizeHandle.addEventListener('mouseenter', () => {
      if (!this.isResizing) {
        this.renderer.setStyle(this.resizeHandle, 'background-color', 'rgba(0, 136, 255, 0.2)');
      }
    });

    this.resizeHandle.addEventListener('mouseleave', () => {
      if (!this.isResizing) {
        this.renderer.setStyle(this.resizeHandle, 'background-color', 'transparent');
      }
    });
  }

  private onMouseDown(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();

    this.isResizing = true;
    this.startX = event.pageX;
    this.startWidth = this.el.nativeElement.offsetWidth;

    this.renderer.setStyle(this.resizeHandle, 'background-color', 'rgba(0, 136, 255, 0.5)');
    this.renderer.addClass(document.body, 'resizing-column');
    this.renderer.setStyle(document.body, 'cursor', 'col-resize');
    this.renderer.setStyle(document.body, 'user-select', 'none');

    document.addEventListener('mousemove', this.boundMouseMove);
    document.addEventListener('mouseup', this.boundMouseUp);
  }

  private onMouseMove(event: MouseEvent): void {
    if (!this.isResizing) return;

    event.preventDefault();
    event.stopPropagation();

    const diff = event.pageX - this.startX;
    const newWidth = Math.max(this.minWidth, this.startWidth + diff);

    this.renderer.setStyle(this.el.nativeElement, 'width', `${newWidth}px`);
    this.renderer.setStyle(this.el.nativeElement, 'min-width', `${newWidth}px`);
    this.renderer.setStyle(this.el.nativeElement, 'max-width', `${newWidth}px`);
  }

  private onMouseUp(event: MouseEvent): void {
    if (!this.isResizing) return;

    event.preventDefault();
    event.stopPropagation();

    this.isResizing = false;
    this.renderer.setStyle(this.resizeHandle, 'background-color', 'transparent');
    this.renderer.removeClass(document.body, 'resizing-column');
    this.renderer.setStyle(document.body, 'cursor', 'default');
    this.renderer.setStyle(document.body, 'user-select', 'auto');

    document.removeEventListener('mousemove', this.boundMouseMove);
    document.removeEventListener('mouseup', this.boundMouseUp);
  }
}
